import { useState } from 'react';
import { SALUTATION_EMPTY_LABEL, SALUTATION_OPTIONS, SALUTATION_SEGMENT_WIDTH } from '../../../../lib/salutations';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { TextField } from '../../../../components/inputs/TextField';
import { Textarea } from '../../../../components/inputs/Textarea';
import { AddressPicker, type AddressValue } from '../../../../components/inputs/AddressPicker';
import { FieldGroup } from '../../../../components/inputs/FieldGroup';
import { FormFieldset } from '../../../../components/inputs/FormFieldset';
import { usePermission } from '../../../../auth/AuthContext';
import { ApiError } from '../../../../lib/apiError';
import { focusValidationError } from '../../../../lib/validationFocus';
import { suppliersApi } from '../suppliersApi';
import { sampleSupplier } from '../utils/sampleSupplier';
import type { SupplierDetail } from '../supplierTypes';

const ID_PREFIX = 'supplier';

/**
 * Register or correct a supplier.
 *
 * **NOT STEPPED.** One panel: what they are called, how to reach them, where they are, and who
 * to ask for. A rail over four field groups would be a progress indicator for a journey the
 * reader can see all of.
 *
 * **THE CONTACT'S NAME IS WRITTEN TO `rsys_pii` AND NEVER READ BACK HERE.** That is the identity
 * split, and it has a visible cost this form states rather than hides: correcting a supplier
 * cannot show the contact's existing name, because the record carries a `PersonId` and nothing
 * else. Leaving the name fields blank on an edit CLEARS the contact; filling them REPLACES it.
 * The form says so, because a reader who assumed the blank meant "unchanged" would detach a
 * contact by saving a phone number.
 */
export function SupplierModal({
  open,
  editing,
  onClose,
  onSaved,
}: {
  open: boolean;
  /** Null creates. */
  editing: SupplierDetail | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  /*
    WHETHER THE READER MAY SEE A NAME AT ALL decides what this form can promise about the
    contact. It does not gate the FIELDS — supplying a name is not a reveal, so anybody who may
    edit a supplier may name its contact — it decides the WORDING, so a reader without the key
    is not told to check a name they cannot be shown.
  */
  const canReadPeople = usePermission('personnel.read');

  const [name, setName] = useState(editing?.name ?? '');
  const [email, setEmail] = useState(editing?.email ?? '');
  const [phone, setPhone] = useState(editing?.phone ?? '');
  const [tin, setTin] = useState(editing?.taxIdentificationNumber ?? '');
  const [street, setStreet] = useState(editing?.streetAddress ?? '');
  const [notes, setNotes] = useState(editing?.notes ?? '');

  const [address, setAddress] = useState<AddressValue>({
    province: editing?.province ?? null,
    cityMunicipality: editing?.cityMunicipality ?? null,
    barangay: editing?.barangay ?? null,
  });

  /*
    THE CONTACT'S NAME STARTS EMPTY ON AN EDIT, AND IT HAS TO. The record carries a `PersonId`,
    not a name — resolving it here would be a second reveal from a form, and the identity split
    is exactly that the name lives in the other store. The help text below says what a blank
    means, which is the only honest way to ship this.
  */
  const [salutation, setSalutation] = useState('');
  const [firstName, setFirstName] = useState('');
  const [middleName, setMiddleName] = useState('');
  const [lastName, setLastName] = useState('');
  const [suffix, setSuffix] = useState('');

  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const anyNamePart =
    firstName.trim().length > 0
    || middleName.trim().length > 0
    || lastName.trim().length > 0
    || suffix.trim().length > 0;

  // ALL FOUR OR NONE: a half-written name resolves to a display name nobody can read, so the
  // server refuses one and the form says which box is missing before the round trip.
  const contactIncomplete = anyNamePart && (firstName.trim().length === 0 || lastName.trim().length === 0);

  const valid = name.trim().length > 0 && !contactIncomplete;
  const canSave = valid && confirmed && !saving;

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    const body = {
      name: name.trim(),
      email: email.trim() || null,
      phone: phone.trim() || null,
      taxIdentificationNumber: tin.trim() || null,
      province: address.province,
      cityMunicipality: address.cityMunicipality,
      barangay: address.barangay,
      streetAddress: street.trim() || null,
      notes: notes.trim() || null,
      contactSalutation: salutation.trim() ? salutation.trim() : null,
      contactFirstName: firstName.trim() || null,
      contactMiddleName: middleName.trim() || null,
      contactLastName: lastName.trim() || null,
      contactSuffix: suffix.trim() || null,
    };

    try {
      if (editing) await suppliersApi.update(editing.id, body);
      else await suppliersApi.create(body);

      onSaved();
    } catch (caught) {
      const failure =
        caught instanceof ApiError ? caught : new ApiError('Could not save this supplier.', 0);

      setError(failure);
      focusValidationError(failure, { idPrefix: ID_PREFIX });
    } finally {
      setSaving(false);
    }
  }

  function generate() {
    const sample = sampleSupplier();

    setName(sample.name);
    setEmail(sample.email ?? '');
    setPhone(sample.phone ?? '');
    setTin(sample.taxIdentificationNumber ?? '');
    setStreet(sample.streetAddress ?? '');
    setNotes(sample.notes ?? '');
    setAddress({
      province: sample.province,
      cityMunicipality: sample.cityMunicipality,
      barangay: sample.barangay,
    });
    setFirstName(sample.contactFirstName ?? '');
    setMiddleName(sample.contactMiddleName ?? '');
    setLastName(sample.contactLastName ?? '');
    setSuffix(sample.contactSuffix ?? '');
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editing ? `Edit ${editing.code}` : 'Register supplier'}
      size="2xl"
      id={`${ID_PREFIX}-modal`}
      autogenerate={{ onGenerate: generate, disabled: saving }}
      confirm={{
        id: `${ID_PREFIX}-confirm`,
        checked: confirmed,
        onChange: setConfirmed,
        disabled: saving,
      }}
      footer={
        <>
          <Button id={`${ID_PREFIX}-cancel`} variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            id={`${ID_PREFIX}-submit`}
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            id={`${ID_PREFIX}-name`}
            label="Name"
            required
            description="What they are called, and what every purchase order naming them prints. Unique among the live suppliers."
            value={name}
            onChange={(e) => setName(e.target.value)}
            error={name.trim() ? error?.fieldError('name') : 'Name the supplier.'}
            disabled={saving}
          />

          <TextField
            id={`${ID_PREFIX}-tax-identification-number`}
            label="TIN"
            description="The Taxpayer Identification Number as printed on what they invoice with. Optional."
            value={tin}
            onChange={(e) => setTin(e.target.value)}
            error={error?.fieldError('taxIdentificationNumber')}
            disabled={saving}
          />

          <TextField
            id={`${ID_PREFIX}-email`}
            label="Email"
            description="Where orders are sent. A shared desk address is fine here — it is the person field that has to be a person."
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={error?.fieldError('email')}
            disabled={saving}
          />

          <TextField
            id={`${ID_PREFIX}-phone`}
            label="Phone"
            description="A number to call about a delivery."
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            error={error?.fieldError('phone')}
            disabled={saving}
          />
        </div>

        <FormFieldset legend="Address">
          <AddressPicker
            id={`${ID_PREFIX}-address`}
            description="One search over every rung. A path may stop at the province or the city — that is a valid answer rather than half an address."
            value={address}
            onChange={setAddress}
            error={error?.fieldError('province') ?? error?.fieldError('cityMunicipality')}
            disabled={saving}
          />

          <TextField
            id={`${ID_PREFIX}-street-address`}
            label="Lot No / Street / Bldg"
            description="The address they write, in the order they write it."
            value={street}
            onChange={(e) => setStreet(e.target.value)}
            error={error?.fieldError('streetAddress')}
            disabled={saving}
          />
        </FormFieldset>

        <FormFieldset legend="Contact person">
          {/*
            WHAT A BLANK MEANS, said before the fields rather than discovered after saving. The
            record carries a `PersonId` and no name, so an edit genuinely cannot show the
            existing one — and a reader who assumed the blank meant "unchanged" would detach a
            contact by correcting a phone number.
          */}
          <p className="-mt-1 mb-1 text-xs text-slate-500">
            {editing
              ? canReadPeople
                ? 'The name lives in the identity store, so this form cannot show the one already recorded — the profile does. Leaving these blank CLEARS the contact; filling them replaces it.'
                : 'The name lives in the identity store and you do not have the grant to be shown it. Leaving these blank CLEARS the contact; filling them replaces it.'
              : 'Optional. This has to be a PERSON — a desk like "Accounts Receivable" belongs in the email or phone above. The name is written to the identity store and never kept on the supplier.'}
          </p>

          <div className="grid gap-4 sm:grid-cols-3">
            <FieldGroup
              label="First name"
              labelFor={`${ID_PREFIX}-contact-first-name`}
              description="The narrow box on the left is a title — Mr., Dr., Atty. It is optional, and it is not part of the name."
              segments={[
                {
                  id: `${ID_PREFIX}-contact-salutation`,
                  label: 'Salutation',
                  value: salutation,
                  onChange: setSalutation,
                  options: SALUTATION_OPTIONS,
                  allowCustom: true,
                  disabled: saving,
                  // The addon gives way, never the grid — a name row is three equal columns.
                  width: SALUTATION_SEGMENT_WIDTH,
                  emptyLabel: SALUTATION_EMPTY_LABEL,
                },
                {
                  id: `${ID_PREFIX}-contact-first-name`,
                  label: 'First name',
                  value: firstName,
                  onChange: setFirstName,
                  error:
                    anyNamePart && firstName.trim().length === 0
                      ? 'Give the first name, or clear the whole contact.'
                      : error?.fieldError('contactFirstName'),
                  disabled: saving,
                },
              ]}
            />

            <TextField
              id={`${ID_PREFIX}-contact-middle-name`}
              label="Middle name"
              value={middleName}
              onChange={(e) => setMiddleName(e.target.value)}
              error={error?.fieldError('contactMiddleName')}
              disabled={saving}
            />

            {/*
              LAST NAME AND SUFFIX ARE ONE VALUE, joined — "Dela Cruz Jr." is one name. Each
              segment keeps its own input id, so a rejection can still be focused.
            */}
            <FieldGroup
              label="Last name"
              description="The narrow box is the suffix — Jr., Sr., III. It is part of the name, not a title."
              segments={[
                {
                  id: `${ID_PREFIX}-contact-last-name`,
                  label: 'Last name',
                  value: lastName,
                  onChange: setLastName,
                  error:
                    anyNamePart && lastName.trim().length === 0
                      ? 'Give the last name, or clear the whole contact.'
                      : error?.fieldError('contactLastName'),
                  disabled: saving,
                },
                {
                  id: `${ID_PREFIX}-contact-suffix`,
                  label: 'Suffix',
                  value: suffix,
                  onChange: setSuffix,
                  error: error?.fieldError('contactSuffix'),
                  disabled: saving,
                  width: 'w-20 shrink-0',
                },
              ]}
            />
          </div>
        </FormFieldset>

        {/*
          FULL WIDTH, outside the grid — the remarks rule. It is the longest value on the form
          and the one most likely to be a sentence.
        */}
        <Textarea
          id={`${ID_PREFIX}-notes`}
          label="Notes"
          description="A free note about them — delivery areas, how long a quotation holds, anything a buyer should know before ordering."
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          error={error?.fieldError('notes')}
          disabled={saving}
        />
      </div>
    </Modal>
  );
}
